import {
  Button,
  EmptyState,
  PageHeader,
  PageTitle,
  StatusTag,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatDate,
  titleCase,
} from '@westhetics/ui'
import { Link, useParams, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { FormSkeleton } from '@/features/clinic-profile/blocks'
import { isApiError } from '@/lib/api'
import { routes, type IntegrationTab } from '@/routes'
import { useIntegration, type Integration } from './api'
import { DeliveriesTab } from './deliveries-tab'
import { integrationStatus, keyHint, lastUsedText } from './labels'
import { OverviewTab } from './overview-tab'
import { PermissionsTab } from './permissions-tab'
import { RequestsTab } from './requests-tab'
import { WebhooksTab } from './webhooks-tab'

/**
 * O app de integração (docs/areas/integracoes.md, "App"). Arquétipo: ficha com abas. O nome em Lexend com a linha
 * da chave e do uso; cada aba trata de um assunto: Visão geral, Permissões, Webhooks, Entregas e Chamadas. A aba
 * fica no endereço (`?tab=`), para o link levar direto a ela.
 */

const TABS: { value: IntegrationTab; label: (i: Integration) => string }[] = [
  { value: 'overview', label: () => 'Visão geral' },
  { value: 'permissions', label: () => 'Permissões' },
  {
    value: 'webhooks',
    label: (i) => (i.webhooks.total > 0 ? `Webhooks ${i.webhooks.total}` : 'Webhooks'),
  },
  {
    value: 'deliveries',
    label: (i) =>
      i.deliveries.failed > 0 ? `Entregas · ${i.deliveries.failed} com falha` : 'Entregas',
  },
  { value: 'requests', label: () => 'Chamadas' },
]

export function IntegrationPage() {
  const { id } = useParams()
  const query = useIntegration(id)
  const integration = query.data
  const notFound = isApiError(query.error) && query.error.status === 404

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Integrações', href: routes.admin.integrations },
          { label: integration?.name ?? '…' },
        ]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.admin.integrations}>Voltar</Link>
          </Button>
        }
      />
      <Page wide>
        {notFound ? (
          <EmptyState
            className="mt-6"
            title="Este app não está nesta clínica"
            description="Ele pode ser de outra clínica, ou o endereço está incompleto. Os apps da clínica ativa estão em Integrações."
            action={
              <Button variant="outline" asChild>
                <Link to={routes.admin.integrations}>Ver os apps</Link>
              </Button>
            }
          />
        ) : query.isError ? (
          <>
            <PageTitle title="App de integração" />
            <LoadError
              className="mt-6"
              title="Não conseguimos abrir este app"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          </>
        ) : !integration ? (
          <>
            <PageTitle title="App de integração" />
            <FormSkeleton blocks={2} />
          </>
        ) : (
          <IntegrationDetail key={integration.id} integration={integration} />
        )}
      </Page>
    </>
  )
}

function IntegrationDetail({ integration }: { integration: Integration }) {
  const [params, setParams] = useSearchParams()
  const fromUrl = params.get('tab') as IntegrationTab | null
  const tab = TABS.some((t) => t.value === fromUrl) ? (fromUrl as IntegrationTab) : 'overview'
  const status = integrationStatus(integration)

  function changeTab(next: string) {
    const nextParams = new URLSearchParams(params)
    nextParams.set('tab', next)
    nextParams.delete('page')
    setParams(nextParams, { replace: true })
  }

  return (
    <>
      <PageTitle
        eyebrow="App de integração"
        title={integration.name}
        meta={[
          keyHint(integration.keyPrefix),
          `criado em ${formatDate(new Date(integration.createdAt))} por ${titleCase(integration.createdBy.name)}`,
          lastUsedText(integration.lastUsedAt),
        ].join(' · ')}
        subtitle={integration.description ?? undefined}
        trailing={<StatusTag level={status.level}>{status.label}</StatusTag>}
      />

      <Tabs value={tab} onValueChange={changeTab} className="mt-6">
        <TabsList aria-label="Assuntos do app">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label(integration)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          <OverviewTab integration={integration} />
        </TabsContent>
        <TabsContent value="permissions">
          <PermissionsTab integration={integration} />
        </TabsContent>
        <TabsContent value="webhooks">
          <WebhooksTab integration={integration} />
        </TabsContent>
        <TabsContent value="deliveries">
          <DeliveriesTab integration={integration} />
        </TabsContent>
        <TabsContent value="requests">
          <RequestsTab integration={integration} />
        </TabsContent>
      </Tabs>
    </>
  )
}
